import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Clock, 
  MapPin, 
  Phone, 
  CheckCircle, 
  Car,
  Wrench,
  Users,
  Award,
  Zap,
  Settings
} from 'lucide-react';

const Services = () => {
  return (
    <>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Unsere Leistungen
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Professionelle Autoglas Reparatur und Austausch für alle Fahrzeugtypen
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Hauptleistungen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Von der Reparatur bis zum kompletten Austausch - wir bieten alle Autoglas Dienstleistungen
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            {/* Autoglas Reparatur */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-xl"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Wrench size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Autoglas Reparatur</h3>
                  <p className="text-blue-600 font-medium">Professionelle Reparatur von Steinschlägen und Rissen</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Unsere erfahrenen Techniker reparieren Steinschläge und Risse in Ihrer Windschutzscheibe 
                oder anderen Autoglaselementen professionell und dauerhaft. Mit modernster Technologie 
                und hochwertigen Materialien stellen wir die ursprüngliche Festigkeit wieder her.
              </p>

              <div className="space-y-3 mb-6">
                <h4 className="font-semibold text-gray-900">Was wir reparieren:</h4>
                <ul className="space-y-2">
                  {[
                    "Steinschläge in der Windschutzscheibe",
                    "Risse bis zu 30cm Länge",
                    "Spider-Cracks (Sternrisse)",
                    "Kleine Kratzer und Beschädigungen",
                    "Seitenscheiben und Heckfenster"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 text-blue-800">
                  <Clock size={20} />
                  <span className="font-semibold">Reparaturzeit: 30-60 Minuten</span>
                </div>
              </div>
            </motion.div>

            {/* Autoglas Austausch */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-xl"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Car size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Autoglas Austausch</h3>
                  <p className="text-blue-600 font-medium">Kompletter Austausch beschädigter Scheiben</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Bei schweren Beschädigungen oder wenn eine Reparatur nicht mehr möglich ist, 
                führen wir den kompletten Austausch Ihrer Autoscheibe durch. Wir verwenden 
                nur hochwertige Original- oder Markenersatzteile für optimale Qualität.
              </p>

              <div className="space-y-3 mb-6">
                <h4 className="font-semibold text-gray-900">Austausch von:</h4>
                <ul className="space-y-2">
                  {[
                    "Windschutzscheiben (alle Marken)",
                    "Seitenscheiben (vorne und hinten)",
                    "Heckfenster",
                    "Dachfenster und Panoramadächer",
                    "Spiegel und kleine Glaselemente"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 text-blue-800">
                  <Clock size={20} />
                  <span className="font-semibold">Austauschzeit: 2-4 Stunden</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Scheibenversiegelung */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gray-50 p-8 rounded-xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Scheibenversiegelung</h3>
                <p className="text-blue-600 font-medium">Professionelle Versiegelung für optimalen Schutz</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Unsere professionelle Scheibenversiegelung bietet optimalen Schutz vor Steinschlägen, 
              UV-Strahlung und Verschmutzung. Die hydrophobe Beschichtung sorgt für bessere Sicht 
              bei Regen und reduziert die Wartung Ihrer Scheiben erheblich.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Vorteile der Versiegelung:</h4>
                <ul className="space-y-2">
                  {[
                    "Schutz vor Steinschlägen",
                    "UV-Schutz für Innenraum",
                    "Hydrophobe Wirkung",
                    "Kratzfestigkeit",
                    "Einfachere Reinigung",
                    "Langlebiger Schutz"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Anwendungsbereiche:</h4>
                <ul className="space-y-2">
                  {[
                    "Windschutzscheibe",
                    "Seitenscheiben",
                    "Heckfenster",
                    "Außenspiegel",
                    "Scheinwerfer",
                    "Rückleuchten"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Zusätzliche Leistungen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir bieten Ihnen umfassende Autoglas Dienstleistungen
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Zap size={48} className="text-blue-600" />,
                title: "Notfall Service",
                description: "24/7 Notfallservice für dringende Reparaturen. Wir sind auch außerhalb der Geschäftszeiten für Sie da.",
                features: ["24/7 Verfügbarkeit", "Schnelle Reaktionszeit", "Mobile Reparatur", "Sofortige Hilfe"]
              },
              {
                icon: <MapPin size={48} className="text-blue-600" />,
                title: "Mobile Reparatur",
                description: "Wir kommen zu Ihnen nach Hause oder an Ihren Arbeitsplatz für die Reparatur.",
                features: ["Vor-Ort Service", "Flexible Termine", "Zeitersparnis", "Professionelle Ausführung"]
              },
              {
                icon: <Settings size={48} className="text-blue-600" />,
                title: "Elektronik Integration",
                description: "Professionelle Integration von Kameras, Sensoren und anderen elektronischen Systemen.",
                features: ["Kamera Kalibrierung", "Sensor Integration", "ADAS Systeme", "Fachgerechte Montage"]
              },
              {
                icon: <Award size={48} className="text-blue-600" />,
                title: "Qualitätsgarantie",
                description: "Alle unsere Arbeiten sind mit umfassender Garantie abgesichert.",
                features: ["Reparatur Garantie", "Material Garantie", "Arbeitsgarantie", "Kundenzufriedenheit"]
              },
              {
                icon: <Users size={48} className="text-blue-600" />,
                title: "Beratung & Planung",
                description: "Persönliche Beratung für die beste Lösung für Ihr Fahrzeug.",
                features: ["Kostenlose Beratung", "Individuelle Lösungen", "Transparente Preise", "Fachliche Expertise"]
              },
              {
                icon: <Clock size={48} className="text-blue-600" />,
                title: "Express Service",
                description: "Schneller Service für Kunden, die es eilig haben.",
                features: ["Sofortige Bearbeitung", "Prioritätsbehandlung", "Schnelle Fertigstellung", "Qualität garantiert"]
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="text-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unser Arbeitsprozess
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              So arbeiten wir für Sie - professionell, schnell und zuverlässig
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Kontakt & Beratung",
                description: "Kontaktieren Sie uns telefonisch oder über unser Kontaktformular für eine erste Beratung."
              },
              {
                step: "02",
                title: "Termin & Angebot",
                description: "Wir vereinbaren einen Termin und erstellen Ihnen ein kostenloses Angebot."
              },
              {
                step: "03",
                title: "Professionelle Ausführung",
                description: "Unsere erfahrenen Techniker führen die Reparatur oder den Austausch professionell durch."
              },
              {
                step: "04",
                title: "Qualitätskontrolle",
                description: "Nach Abschluss der Arbeiten erfolgt eine umfassende Qualitätskontrolle und Übergabe."
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">{process.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Brauchen Sie eine Autoglas Reparatur?
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Kontaktieren Sie uns jetzt für ein kostenloses Angebot. Wir sind für Sie da 
              in Mannheim, Frankfurt und Stuttgart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:062171895675"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                <Phone size={24} />
                <span>Jetzt anrufen: 0621 71895675</span>
              </a>
              <a
                href="mailto:info@autoglass-mannheim.de"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300"
              >
                E-Mail senden
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services; 